import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import '@/lib/chartjs-register';
import { convertChartOptions } from '@/lib/chartjs-register';
import { ChartWidget, useChartColors } from '@/components/ui/shared/ChartWidget';
import { userService } from '../_services';
import ColorJson from '../../src/whitelabel.json';
import { Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';

/**
 * BarListChart - Functional component for Bar/List toggle widget
 * 
 * Replaces the class-based Bar_Listchart component with modern React patterns:
 * - useState for state management
 * - useEffect for data fetching and cleanup
 * - useCallback for memoized handlers
 * - AbortController for canceling pending requests
 */
function BarListChart({ data, handlechangea, _Index }) {
    // State - only show loader if data is ready to fetch
    const [chartData, setChartData] = useState([]);
    const [listData, setListData] = useState([]);
    const [listHeaders, setListHeaders] = useState([]);
    const [legendValue, setLegendValue] = useState('');
    const [legendText, setLegendText] = useState('');
    const [isLoading, setIsLoading] = useState(!!(data?.RequestJson && data?.url));
    const [error, setError] = useState(null);
    const [noDataMessage, setNoDataMessage] = useState('');

    // Refs
    const abortControllerRef = useRef(null);
    const prevRequestJsonRef = useRef(null);

    // Get user data
    const userdata = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || {};
        } catch {
            return {};
        }
    }, []);

    // Determine if RxHacker theme
    const isRxHacker = useMemo(() => {
        const host = window.location.host.split('.').slice(-2).join('.').toLowerCase();
        return host === "rxhacker.com" ||
               host === "nulifespanrx.com" ||
               host === "localhost:5464" || 
               window.location.host.toLowerCase() === "tempfilter.23byt.es" || 
               window.location.host.toLowerCase() === "reporting.nulifespanrx.com";
    }, []);

    // Get theme colors
    const getChartColors = useChartColors();

    // Handle API response
    const handleResponse = useCallback((response) => {
        return response.text().then(text => {
            try {
                return text && JSON.parse(text);
            } catch {
                return [];
            }
        });
    }, []);

    // Format legend value with dollar sign if needed
    const formatLegendValue = useCallback((value, title) => {
        if (!value) return '';
        
        const dollarTitles = ['Average Patient Savings ($)', 'Total Patient Savings ($)'];
        const percentTitles = ['Average Patient Savings (%)'];
        
        if (dollarTitles.includes(title)) {
            return userService.AddDollar(value, '$');
        }
        if (percentTitles.includes(title)) {
            return userService.AddDollar(value, '%');
        }
        return value;
    }, []);

    // Export widget data as CSV
    const handleExport = useCallback(() => {
        try {
            const widgetTitle = data.title || 'export';
            const tableData = [];
            
            // Build CSV from list data
            if (listHeaders.length > 0) {
                tableData.push(listHeaders.join(','));
            }
            
            listData.forEach(row => {
                const rowData = row.map((cell, idx) => {
                    // Add space prefix for date columns to prevent Excel auto-formatting
                    const needsPrefix = idx === 0 && [
                        'Transactions in Kansas', 'Total Transactions', 'Total RxHacker Signups', 'Total Enrollment Center Signups',
                        'Compensable Transactions', 'Client Access Link Signups', 'API Signups',
                        'Paid Transactions', 'Average Patient Savings (%)', 'Total Patient Savings ($)',
                        'Average Patient Savings ($)', 'Paid Transactions by Group Number', "Unique Member ID's"
                    ].includes(widgetTitle);
                    
                    const cellValue = String(cell || '').split(',').join('');
                    return needsPrefix ? ` ${cellValue}` : cellValue;
                });
                tableData.push(rowData.join(','));
            });
            
            const csvContent = tableData.join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `${widgetTitle}.csv`;
            link.href = url;
            document.body.appendChild(link);
            
            setTimeout(() => {
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }, 0);
        } catch (err) {
        }
    }, [data.title, listData, listHeaders]);

    // Map display column names to backend filter keys
    const getBackendKeyForColumn = useCallback((columnHeader) => {
        // Mapping for common column headers to backend keys
        const mapping = {
            // Pharmacy columns
            'Name': 'PharmacyName',
            'Pharmacy Name': 'PharmacyName', // For Patient Savings widget
            'City': 'PhCity',
            'State': 'PhState',
            'Zip(*)': 'PhZip',
            
            // Prescriber columns
            'Prescriber Name': 'Prescriber',
            'Prescriber Address': 'PrAddress',
            'Prescriber City': 'PrCity',
            'Prescriber State': 'PrState',
            'Prescriber Zip': 'PrZip',
            
            // Wholesaler/Distributor columns
            'Wholesaler Name': 'WholesalerName',
            'Distributor Name': 'DistributorName',
            'Group Number': 'GroupNumber',
            
            // Drug columns
            'Drug Name': 'DrugName',
            
            // Keep these as-is if they appear
            'PharmacyName': 'PharmacyName',
            'Prescriber': 'Prescriber',
            'PrCity': 'PrCity',
            'PrState': 'PrState',
            'PrZip': 'PrZip',
            'PhCity': 'PhCity',
            'PhState': 'PhState',
            'PhZip': 'PhZip',
            'DrugName': 'DrugName',
            'WholesalerName': 'WholesalerName',
            'DistributorName': 'DistributorName',
            'GroupNumber': 'GroupNumber'
        };
        
        return mapping[columnHeader] || columnHeader;
    }, []);

    // Handle list item click (for filtering)
    const handleListItemClick = useCallback((row, rowIdx, cellIdx = 0) => {
        // Disable clicking for widgets with DataType "NA" (e.g. Total Transactions)
        if (data.DataType === 'NA') {
            return;
        }
        
        if (handlechangea && listHeaders.length > 0) {
            const columnHeader = listHeaders[cellIdx];
            
            // Map the display column name to backend key
            const backendKey = getBackendKeyForColumn(columnHeader);
            
            // Only create filter if we have a valid heading
            if (!backendKey || backendKey === 'undefined') {
                return;
            }
            
            // Create filter event
            const filterData = {
                heading: backendKey,
                value: row[cellIdx],
            };
            
            handlechangea(filterData);
        }
    }, [handlechangea, listHeaders, getBackendKeyForColumn, data.DataType]);

    // Handle bar click (for filtering via chart clicks)
    const handleBarClick = useCallback((event, elements) => {
        if (!handlechangea || !elements || elements.length === 0) return;
        
        try {
            const element = elements[0];
            const chart = element.element?.$context?.chart;
            const label = chart?.data?.labels?.[element.index];
            const dataType = data.DataType;
            
            // Only add filter for GroupNumber type (matching production-v3 logic)
            if (dataType && dataType !== 'NA' && dataType === 'GroupNumber' && label) {
                const filterData = {
                    heading: dataType,
                    value: label,
                };
                handlechangea(filterData);
            }
        } catch (error) {
        }
    }, [handlechangea, data.DataType]);

    // Build chart options
    const buildChartOptions = useCallback((isDateChart, xLabel, yLabel, interval) => {
        return {
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: true,
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    titleColor: '#1d252d',
                    bodyColor: '#1d252d',
                    borderColor: '#e2e8f0',
                    borderWidth: 1,
                    padding: 12,
                    cornerRadius: 8,
                    titleFont: {
                        size: 12,
                        weight: 'normal',
                    },
                    bodyFont: {
                        size: 14,
                        weight: 'bold',
                    },
                },
            },
            scales: {
                y: {
                    beginAtZero: true,
                    border: {
                        display: false,
                    },
                    grid: {
                        display: false,
                        drawBorder: false,
                    },
                    ticks: {
                        color: '#94a3b8',
                        font: {
                            size: 12,
                        },
                        precision: 0,
                        ...(data.id === 'WKansasTransaction' ? { stepSize: 1 } : {}),
                    },
                    title: {
                        display: !!yLabel,
                        text: yLabel,
                        color: '#94a3b8',
                        font: {
                            size: 12,
                        },
                    },
                },
                x: {
                    border: {
                        display: false,
                    },
                    grid: {
                        display: false,
                        drawBorder: false,
                    },
                    ticks: {
                        display: false,
                        color: '#94a3b8',
                        font: {
                            size: 11,
                        },
                        maxRotation: 45,
                        minRotation: 0,
                        maxTicksLimit: 10,
                    },
                    title: {
                        display: true,  // Show x-axis title (Date, Group Number, etc.)
                        text: xLabel,
                        color: '#94a3b8',
                        font: {
                            size: 12,
                            weight: 'normal',
                        },
                    },
                },
            },
            interaction: {
                intersect: false,
                mode: 'index',
            },
            onHover: (event, elements) => {
                const canvas = event.native?.target;
                if (canvas) {
                    canvas.style.cursor = elements.length > 0 ? 'pointer' : 'default';
                }
            },
            onClick: handleBarClick,
        };
    }, [handleBarClick]);

    // Fetch data
    const fetchData = useCallback(async () => {
        // Cancel any previous pending request
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Create new AbortController
        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsLoading(true);
        setError(null);
        setNoDataMessage('Loading. Please wait.');

        try {
            const response = await fetch(data.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'requestHeader': data.RequestJson1,
                    'X-Browser-Url': window.location.href,
                },
                body: data.RequestJson,
                signal: controller.signal,
            });

            const apidata = await handleResponse(response);

            if (controller.signal.aborted) return;

            if (!apidata || !apidata[0]) {
                setNoDataMessage('No data available');
                setIsLoading(false);
                return;
            }

            // Process legend data
            try {
                if (apidata[0].legend?.length > 0) {
                    const rawValue = apidata[0].legend[0].legendValue;
                    setLegendValue(formatLegendValue(rawValue, data.title));
                    setLegendText(apidata[0].legend[0].legendText);
                } else {
                    setLegendValue('0');
                    setLegendText('');
                }
            } catch {
                setLegendValue('0');
                setLegendText('');
            }

            // Process chart data
            try {
                const columnDataSet = apidata[0].columnDataSet?.[0] || apidata[0].columnDataSet1?.[0];
                
                if (columnDataSet) {
                    const hasData = columnDataSet.data?.length > 1;
                    
                    if (!hasData) {
                        setNoDataMessage('No Data Available');
                    } else {
                        setNoDataMessage('');
                    }

                    // Get theme-appropriate colors
                    const primaryColor = data.ColorCode || (isRxHacker 
                        ? ColorJson.ApplicationColor.Rxhacker_secondary_color 
                        : ColorJson.ApplicationColor.Glic_secondary_color);
                    
                    const hoverColor = isRxHacker 
                        ? ColorJson.ApplicationColor.Rxhacker_extra_color 
                        : '#FF8B6C';

                    setChartData([{
                        labels: columnDataSet.labels,
                        datasets: [{
                            label: columnDataSet.toplabel,
                            data: columnDataSet.data,
                            backgroundColor: primaryColor,
                            borderColor: primaryColor,
                            hoverBackgroundColor: hoverColor,
                            borderRadius: 4,
                            barThickness: 'flex',
                            maxBarThickness: 120,
                        }],
                    }]);
                }
            } catch (err) {
            }

            // Process list data
            try {
                const listDataSet = apidata[0].listDataSet;
                if (listDataSet && listDataSet.length > 0) {
                    // First row is headers
                    const headers = listDataSet[0]?.datasets?.map(d => d.data) || [];
                    setListHeaders(headers);

                    // Rest is data
                    const rows = listDataSet.slice(1).map(row => 
                        row.datasets?.map(d => d.data) || []
                    );
                    setListData(rows);
                }
            } catch (err) {
            }

            setIsLoading(false);
        } catch (err) {
            if (err.name === 'AbortError') {
                return; // Expected cancel
            }
            setError('Error loading data');
            setIsLoading(false);
        }
    }, [data.url, data.RequestJson, data.RequestJson1, data.ColorCode, data.title, 
        handleResponse, formatLegendValue, isRxHacker]);

    // Fetch data when RequestJson changes (with null guard)
    useEffect(() => {
        if (data.type === 'Bar_List' && data.RequestJson && data.url && 
            data.RequestJson !== prevRequestJsonRef.current) {
            prevRequestJsonRef.current = data.RequestJson;
            fetchData();
        }
    }, [data.type, data.RequestJson, data.url, fetchData]);

    // Initial fetch (with null guard and proper deps)
    useEffect(() => {
        if (data.type === 'Bar_List' && data.RequestJson && data.url) {
            fetchData();
        }
        
        // Cleanup on unmount
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [data.type, data.RequestJson, data.url, fetchData]);

    // Build chart options
    const chartOptions = useMemo(() => {
        return buildChartOptions(
            chartData[0]?.datasets?.[0]?.data?.some(d => d?.x), // isDateChart
            data.XlabelString,
            data.YlabelString,
            data.interval
        );
    }, [chartData, data.XlabelString, data.YlabelString, data.interval, buildChartOptions]);

    // Render chart content
    const chartContent = useMemo(() => {
        if (!chartData.length || !chartData[0]?.labels?.length) {
            return null;
        }

        return (
            <div className="h-full w-full">
                <Bar 
                    data={chartData[0]} 
                    options={convertChartOptions(chartOptions)} 
                />
            </div>
        );
    }, [chartData, chartOptions]);

    // Additional controls (info tooltip for Broker)
    const additionalControls = useMemo(() => {
        if (userdata.isBrokerTypeUser && data.title === 'Paid Transactions') {
            return (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                            <Info className="h-4 w-4" />
                        </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>This widget shows paid claims (transactions with marketing fee)</p>
                    </TooltipContent>
                </Tooltip>
            );
        }
        return null;
    }, [userdata.isBrokerTypeUser, data.title]);

    return (
        <TooltipProvider>
            <ChartWidget
                title={data.title}
                subtitle={data.Subtitle}
                legendValue={legendValue}
                legendLabel=""
                isLoading={isLoading}
                error={error}
                noDataMessage={noDataMessage || 'No data available'}
                chartContent={chartContent}
                chartType="Bar"
                listData={listData}
                listHeaders={listHeaders}
                onListItemClick={data.DataType === 'NA' ? null : handleListItemClick}
                onExport={handleExport}
                exportTitle={data.title}
                additionalControls={additionalControls}
                showToggle={true}
                defaultView="chart"
                minHeight="300px"
            />
        </TooltipProvider>
    );
}

export default BarListChart;

